/**
 * Branded report view of the Nested Egg.
 * Hierarchy: Title → Success → Big Idea → Scaffolds side by side.
 */
import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

export default function ReportViewFormat({ tiles, mapName, measurement, clientName, updatedAt, mapId }) {
  const [exporting, setExporting] = useState(false);
  const bigIdea = tiles.find((t) => t.zone === 'BIG_IDEA');
  const scaffold1 = tiles.find((t) => t.zone === 'SCAFFOLDING_1');
  const scaffold2 = tiles.find((t) => t.zone === 'SCAFFOLDING_2');
  const proof1 = tiles.filter((t) => t.zone === 'PROOF_1');
  const proof2 = tiles.filter((t) => t.zone === 'PROOF_2');

  const [zoom, setZoom] = useState(1);
  const MIN_Z = 0.4, MAX_Z = 1.5;

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="flex-1 relative overflow-hidden flex flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto bg-white">
        <div
          className="max-w-5xl mx-auto px-8 py-4"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
        >
          {/* Header: Title + client/date */}
          <div className="flex items-baseline justify-between pb-2 mb-3">
            <h1 className="text-xl font-heading font-bold text-black uppercase tracking-wide">
              {mapName}
            </h1>
            <div className="flex items-center gap-2 text-[10px] text-txt-secondary">
              {clientName && <span>{clientName}</span>}
              {updatedAt && <span>{formatDate(updatedAt)}</span>}
              <img src="/bcd-icon.png" alt="" className="h-3 w-auto opacity-40" />
            </div>
          </div>

          {/* Success metric */}
          {measurement && (
            <div className="bg-gold/20 border-l-4 border-gold-dark rounded-r-lg px-4 py-2 mb-3">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-0.5">
                What Does Success Look Like?
              </p>
              <p className="text-base text-black font-semibold">{measurement}</p>
            </div>
          )}

          {/* Big Idea */}
          <div className="text-center mb-3">
            <div className="inline-block rounded-lg px-4 py-2 shadow-sm"
              style={{ background: 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)', border: '2px solid #C45E10' }}>
              <p className="text-[7px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-0.5">The Big Idea</p>
              <p className="text-base font-heading font-bold text-black uppercase">{bigIdea?.text || 'Not set'}</p>
            </div>
          </div>

          {/* Two scaffolds side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Scaffold 1 */}
            <div>
              <div className="rounded-lg px-3 py-2 mb-2 shadow-sm"
                style={{ background: 'linear-gradient(90deg, #1D9E75 0%, #3FBF94 50%, #1D9E75 100%)', border: '2px solid #157A5A' }}>
                <p className="text-[7px] font-heading font-bold uppercase tracking-widest text-teal-dark mb-0.5">Scaffold 1</p>
                <p className="text-sm font-heading font-bold text-black uppercase">{scaffold1?.text || 'Not set'}</p>
              </div>
              {proof1.length > 0 ? (
                <div className="space-y-1.5">
                  {proof1.map((t) => (
                    <div key={t.id} className="bg-teal/12 border border-teal-dark/25 rounded-lg px-3 py-1.5">
                      <span className="text-sm font-semibold text-black">{t.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-txt-secondary italic">No supporting concepts</p>
              )}
            </div>

            {/* Scaffold 2 */}
            <div>
              <div className="rounded-lg px-3 py-2 mb-2 shadow-sm"
                style={{ background: 'linear-gradient(90deg, #E87722 0%, #F09A50 50%, #E87722 100%)', border: '2px solid #C45E10' }}>
                <p className="text-[7px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-0.5">Scaffold 2</p>
                <p className="text-sm font-heading font-bold text-black uppercase">{scaffold2?.text || 'Not set'}</p>
              </div>
              {proof2.length > 0 ? (
                <div className="space-y-1.5">
                  {proof2.map((t) => (
                    <div key={t.id} className="bg-gold/12 border border-gold-dark/25 rounded-lg px-3 py-1.5">
                      <span className="text-sm font-semibold text-black">{t.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-txt-secondary italic">No supporting concepts</p>
              )}
            </div>
          </div>

          {/* Clarity Statement */}
          {bigIdea && scaffold1 && scaffold2 && (
            <div className="border-t-2 border-gold mt-4 pt-3">
              <p className="text-[9px] font-heading font-bold uppercase tracking-widest text-gold-dark mb-1.5">
                Your Clarity Statement
              </p>
              <p className="text-sm text-txt-primary leading-relaxed">
                {measurement && (vb(measurement)
                  ? <>This is designed to <strong>{lf(measurement)}</strong>. </>
                  : <>This delivers <strong>{lf(measurement)}</strong>. </>)}
                The Big Idea: <strong>{lf(bigIdea.text)}</strong>.
                {' '}It's built on two scaffolds: <strong>{lf(scaffold1.text)}</strong>, and <strong>{lf(scaffold2.text)}</strong>.
                {[...proof1, ...proof2].length > 0 && <> Supporting concepts: {[...proof1, ...proof2].map((t, i, arr) => (
                  <span key={t.id}>{i > 0 && i < arr.length - 1 && ', '}{i > 0 && i === arr.length - 1 && ', and '}{lf(t.text)}</span>
                ))}.</>}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Zoom + Export controls — OUTSIDE the scroll container so they stay fixed */}
      <div className="absolute bottom-3 left-3 z-30 flex items-center gap-2" data-no-print>
        <div className="flex items-center bg-white shadow-sm border border-navy/10 rounded-lg overflow-hidden">
          <button onClick={() => setZoom((z) => Math.max(MIN_Z, +(z - 0.05).toFixed(2)))}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-r border-navy/10 font-medium">&minus;</button>
          <span className="px-3 py-2 text-xs text-txt-secondary tabular-nums min-w-[48px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(MAX_Z, +(z + 0.05).toFixed(2)))}
            className="px-2.5 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-gray-50 transition-colors border-l border-navy/10 font-medium">+</button>
        </div>
        <button
          onClick={async () => {
            if (!mapId || exporting) return;
            setExporting(true);
            try {
              const token = localStorage.getItem('token');
              const base = window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api';
              const res = await fetch(`${base}/export-pdf/${mapId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!res.ok) throw new Error('Export failed');
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${mapName || 'Clarity Map'} - Report.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('PDF export error:', err);
              alert('Failed to generate PDF. Please try again.');
            } finally {
              setExporting(false);
            }
          }}
          disabled={exporting}
          className="flex items-center gap-1.5 bg-navy text-white h-9 px-4 rounded-lg font-heading font-bold uppercase text-xs hover:bg-navy-light transition-colors shadow-sm disabled:opacity-50"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {exporting ? 'Generating...' : 'Export PDF'}
        </button>
      </div>

    </div>
  );
}

function lf(str) { if (!str) return ''; if (/^[A-Z]{2}/.test(str)) return str; return str.charAt(0).toLowerCase() + str.slice(1); }
function vb(str) { return /^(increase|improve|reduce|create|build|achieve|ensure|drive|get|make|deliver|grow|launch|secure|establish|develop|implement|generate|expand|maximize|minimize|optimize|strengthen|accelerate|transform|enable|empower|earn|enter|save|cut|boost|raise|lower|maintain|sustain|reach|hit|exceed|double|triple|prove|show|demonstrate|win|close|convert|retain|acquire|upsell|automate|streamline|simplify|eliminate|prevent|protect|scale|align|integrate|unify|consolidate|engage|activate|inspire|motivate|educate|train|teach|convince|persuade)\b/i.test(str); }
